import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Search as SearchIcon } from "lucide-react";
import Paywall from "../components/Paywall.jsx";
import { useIsPremium } from "../lib/premium";

export default function Drugs() {
  const premium = useIsPremium();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [total, setTotal] = useState(null);

  useEffect(() => { api.get("/stats/counts").then((r) => setTotal(r.data.drugs)).catch(() => {}); }, []);

  useEffect(() => { if (premium) api.get("/drugs/categories").then((r) => setCats(r.data)).catch(() => {}); }, [premium]);
  useEffect(() => {
    if (!premium) return;
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    api.get("/drugs", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [q, cat, premium]);

  if (!premium) return <Paywall feature="The Drug Reference" />;

  return (
    <div className="space-y-6" data-testid="drugs-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Formulary</div>
        <div className="flex items-center justify-between flex-wrap gap-2 mt-1">
          <h1 className="font-heading text-3xl font-bold text-[#111827]">Drug Reference</h1>
          <Badge className="bg-slate-100 text-slate-700 border-0">{items.length} of {total ?? items.length}</Badge>
        </div>
        <p className="text-slate-600 text-sm mt-1">SAMF/STG-aligned dosing, indications, interactions and pregnancy info.</p>
      </div>

      <div className="relative max-w-lg">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, class, indication..." className="pl-9 h-11 bg-white" data-testid="drugs-search" />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <button onClick={() => setCat("")} className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${
          !cat ? "bg-[#0B5FFF] text-white" : "bg-white border border-slate-200 text-slate-700"
        }`} data-testid="drug-cat-all">All</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c === cat ? "" : c)} className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${
            cat === c ? "bg-[#0B5FFF] text-white" : "bg-white border border-slate-200 text-slate-700"
          }`} data-testid={`drug-cat-${c}`}>{c}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((d) => (
          <Link key={d.drug_id} to={`/app/drugs/${d.drug_id}`} data-testid={`drug-card-${d.drug_id}`}>
            <Card className="border-slate-200 hover:border-[#0B5FFF]/40 hover:shadow-sm h-full">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-[#EFF4FF] text-[#111827] border-0 text-[10px]">{d.category}</Badge>
                  {d.route && <span className="text-xs text-slate-500">{d.route}</span>}
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#111827]">{d.name}</h3>
                <div className="text-xs text-slate-500 mt-1">{d.drug_class}</div>
                {d.indication && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{d.indication}</p>}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
      {items.length === 0 && <div className="text-center py-20 text-slate-500 text-sm">No drugs found.</div>}
    </div>
  );
}
